import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Provider, useSelector } from 'react-redux'
import { NotificationContainer } from 'react-notifications'
import { setupStore } from '../store/store'
import { setMiscData } from '../../group_leader/store/miscSlice'
import { setRoundId } from '../store/wizardSlice'
import { makeT } from '../../group_leader/helpers/i18n'
import WizardHeader from './wizard/WizardHeader'
import GamesStep from './steps/GamesStep'
import PlayersStep from './steps/PlayersStep'
import PairingsStep from './steps/PairingsStep'
import CourseTeeStep from './steps/CourseTeeStep'
import ReviewStep from './steps/ReviewStep'

const t = makeT('group_leader.event_setup')

// One entry per step in STEPS, filled as the step branches land; a step with no entry renders nothing.
const STEP_COMPONENTS = {
  games: GamesStep,
  players: PlayersStep,
  pairings: PairingsStep,
  course_tee: CourseTeeStep,
  review: ReviewStep,
}

const EventSetupApp = () => {
  const currentStep = useSelector((state) => state.wizard.currentStep)
  const StepComponent = STEP_COMPONENTS[currentStep]
  const mainRef = useRef(null)
  const firstRender = useRef(true)

  // The control that advanced the step unmounts, so move focus too or it falls to <body>.
  useEffect(() => {
    window.scrollTo(0, 0)
    // Not on the first render -- a step that autofocuses its own first field would lose it.
    if (firstRender.current) { firstRender.current = false; return }
    mainRef.current?.focus()
  }, [ currentStep ])

  return (
    <div className="gl-event-setup-page-wrap">
      {/* The layout has no h1 (a11y checklist 1); hidden because the step bar is the visual title. */}
      <h1 className="sr-only">{t('page_title')}</h1>
      <WizardHeader />
      <main className="gl-event-setup-step-content" ref={mainRef} tabIndex={-1}>
        {StepComponent && <StepComponent />}
      </main>
      <NotificationContainer />
    </div>
  )
}

// Every prop is boot data that v3BaseQuery and the steps read back out of state.misc.
const GroupLeaderEventSetupRoot = (props) => {
  const storeRef = useRef(null)

  if (!storeRef.current) {
    storeRef.current = setupStore()
    storeRef.current.dispatch(setMiscData(props))
    if (props.roundId) { storeRef.current.dispatch(setRoundId(props.roundId)) }

    // REACT-028: expose the store so jQuery on the page can drive the wizard, as the roster module does.
    window.glg = window.glg || {}
    window.glg.groupLeaderEventSetup = { dispatch: storeRef.current.dispatch }
  }

  return (
    <Provider store={storeRef.current}>
      <EventSetupApp />
    </Provider>
  )
}

GroupLeaderEventSetupRoot.propTypes = {
  customerId: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  groupId: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  // The group's roster league, which the Golfers step lists and enrols from
  groupRosterLeagueId: PropTypes.string,
  leagueId: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  roundId: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  csrfToken: PropTypes.string,
  tmsApiBaseUrl: PropTypes.string,
  tmsApiToken: PropTypes.string,
  leagueDashboardUrl: PropTypes.string,
  leagueResultsUrl: PropTypes.string,
  // Round-status endpoint, with ROUND_PLACEHOLDER for the id until one exists
  roundStatusUrl: PropTypes.string,
  phoneFieldName: PropTypes.string,
  eventName: PropTypes.string,
  currencySymbol: PropTypes.string,
}

export default GroupLeaderEventSetupRoot
